import { usePositionDetail } from 'apis/hooks'
import { DeactivatedStatusModal } from 'components/misc/deactivated-status'
import {
  Column,
  ResponsiveDisplay,
  Row,
  SpaceBetween,
} from 'components/common/layout'
import { FunctionModal } from 'components/common/page-modal'
import { PositionButton } from 'components/positions/position-button'
import { PositionDetail } from 'components/positions/position-detail'
import { PositionHeader } from 'components/positions/position-header'
import { analytics } from 'infra/analytics'
import { observer } from 'mobx-react'
import React, { Suspense, useEffect, useState } from 'react'
import { useStores } from 'stores/globals'
import styled from 'styled-components'
import {
  updateStartupPositionInterested,
  viewStartupPosition,
} from 'utils/handlers'
import { usePositionIsInterestedUpdate } from 'utils/hooks'
import { PositionTable } from 'components/positions/position-table'
import { BottomStickyBar } from 'components/common/bottom-sticky-bar'
import { StartupSimpleBanner } from 'containers/positions/startup-simple-banner'
import { PositionAnalyzeButton } from 'components/positions/analysis/analyze-button'
import Tippy from '@tippyjs/react'
import { EmploymentRewardNotice } from 'components/positions/employment-reward-notice'

const PositionModalContent: React.FC<{
  positionId: number
  onClose: () => void
}> = ({ positionId, onClose }) => {
  const { data } = usePositionDetail(positionId)
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false)
  usePositionIsInterestedUpdate(
    [positionId],
    `/startup-positions/${positionId}`,
  )
  useEffect(() => {
    viewStartupPosition(positionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (!data) return null
  return (
    <>
      <StartupSimpleBanner startup={data.startup} width={860} />
      <ContentContainer>
        <PositionHeaderContainer>
          <ModalLayout>
            <SpaceBetween style={{ gap: 24, alignItems: 'start' }}>
              <PositionHeader data={data} />
              <ResponsiveDisplay isDisableMobile>
                <Suspense>
                  <PositionAnalyzeButton position={data} />
                </Suspense>
              </ResponsiveDisplay>
            </SpaceBetween>
            <PositionTable data={{ ...data, hasCto: data.startup.hasCto }} />
          </ModalLayout>
        </PositionHeaderContainer>
        <ModalLayout>
          <StickyLayout>
            <Column style={{ flex: 1 }}>
              <PositionDetail
                data={{
                  ...data,
                  location: data.location?.name || data.startup.location,
                  address: data.address || data.startup.address,
                }}
                width={860}
              />
            </Column>
          </StickyLayout>
        </ModalLayout>
        <Tippy
          maxWidth={1000}
          visible
          interactive
          content={<EmploymentRewardNotice />}
        >
          <BottomStickyBar>
            <BottomWrapper>
              <PositionButton
                data={data}
                openDeactivatedModal={() => setIsDeactivatedModalOpen(true)}
                onSaveClick={() => {
                  updateStartupPositionInterested(data.id, !data.isInterested)
                  analytics.track('jobSearch_jsu_click_saveJobPost', {
                    startupId: data.startup.id,
                    startupName: data.startup.name,
                    positionId: data.id,
                    positionName: data.name,
                  })
                }}
                onApplyClick={() => {
                  analytics.track('jobSearch_jsu_click_applyJobPost', {
                    startupId: data.startup.id,
                    startupName: data.startup.name,
                    positionId: data.id,
                    positionName: data.name,
                  })
                  onClose()
                }}
              />
            </BottomWrapper>
          </BottomStickyBar>
        </Tippy>
        {isDeactivatedModalOpen && (
          <DeactivatedStatusModal
            onClose={() => setIsDeactivatedModalOpen(false)}
          />
        )}
      </ContentContainer>
    </>
  )
}

export const PositionModal: React.FC = observer(() => {
  const { positionModalStore } = useStores()
  if (!positionModalStore.isOpen) return null

  return (
    <FunctionModal
      width={1060}
      pageLink={`/positions/${positionModalStore.id}`}
      clickPageLink={() => {
        positionModalStore.closeModal()
      }}
      onClose={() => positionModalStore.closeModal()}
      newTab
    >
      <React.Suspense>
        <PositionModalContent
          positionId={positionModalStore.id}
          onClose={() => positionModalStore.closeModal()}
        />
      </React.Suspense>
    </FunctionModal>
  )
})

const BottomWrapper = styled(Row)`
  width: 860px;
  margin: auto;
  gap: 12px;
  padding-top: 16px;
  padding-bottom: 16px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    width: 100%;
  }
`

const PositionHeaderContainer = styled(Row)`
  padding: 60px 0 60px;
`

const ModalLayout = styled(Column)`
  gap: 40px;
  width: 860px;
  margin: 0 auto;
  @media ${(p) => p.theme.deviceSize.mobile} {
    width: 100%;
  }
`

const StickyLayout = styled(Row)`
  align-items: flex-start;
  gap: 20px;
  padding-bottom: 60px;
`

const ContentContainer = styled(Column)`
  padding-bottom: 60px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding-right: 20px;
    padding-left: 20px;
  }
`
